import { Chart } from 'chart.js';
import { useRef, useEffect } from 'react';
import {
  formatShortDate,
  durationToString,
  msToHours,
  hoursToMs,
  median,
  average as avg,
} from '../../../../utils';

const defaultFontColor = '#CCC';

const LEFT_Y_AXIS = 'left-y-axis';
const RIGHT_Y_AXIS = 'right-y-axis';
const TIME_TO_REVIEW_TITLE = 'Median time to review';

const datasetConfig = {
  xAxisID: 'x-axis',
  pointHoverRadius: 5,
  pointHitRadius: 10,
  borderWidth: 2,
  lineTension: 0,
};

const getTotalReviewsDataset = (reviews) => ({
  ...datasetConfig,
  yAxisID: LEFT_Y_AXIS,
  data: reviews.map((r) => r.totalReviews),
  label: 'Total reviews',
  order: 0,
  borderColor: 'rgba(0, 172, 193, 1)',
  backgroundColor: 'rgba(0, 172, 193, 0.2)',
  fill: 'origin',
});

const getTimeToReviewDataset = (reviews) => ({
  ...datasetConfig,
  yAxisID: RIGHT_Y_AXIS,
  data: reviews.map((r) => msToHours(r.timeToReview)), // Passing to hours to force Chart.js pick better ticks for Y axis
  label: TIME_TO_REVIEW_TITLE,
  order: 1,
  borderColor: 'rgba(233, 30, 99, 1)',
  backgroundColor: 'rgba(233, 30, 99, 0.2)',
  fill: 'origin',
});

const getTotalCommentsDataset = (reviews) => ({
  ...datasetConfig,
  yAxisID: LEFT_Y_AXIS,
  data: reviews.map((r) => r.totalComments),
  label: 'Total comments',
  order: 2,
  borderColor: 'rgba(112, 0, 220, 1)',
  backgroundColor: 'rgba(112, 0, 220, 0.2)',
  fill: 'origin',
  hidden: true,
});

const getTooltipOptions = () => ({
  mode: 'x',
  position: 'nearest',
  backgroundColor: 'rgba(0, 0, 0, 0.9)',
  intersect: false,
  titleSpacing: 6,
  xPadding: 8,
  yPadding: 8,
  callbacks: {
    label: (item, data) => {
      const title = data.datasets[item.datasetIndex].label;
      if (title === TIME_TO_REVIEW_TITLE) return ` ${title}: ${durationToString(hoursToMs(item.yLabel))}`;
      return ` ${title}: ${item.yLabel}`;
    },
    labelColor: (item, chart) => {
      const { borderColor } = chart.config.data.datasets[item.datasetIndex];
      return { borderColor, backgroundColor: borderColor };
    },
  },
});

const getLeftYAxisOptions = () => ({
  id: LEFT_Y_AXIS,
  type: 'linear',
  position: 'left',
  ticks: {
    min: 0,
  },
  gridLines: {
    color: 'rgba(128, 128, 128, 0.1)',
    lineWidth: 1,
  },
});

const getRightYAxisOptions = () => ({
  id: RIGHT_Y_AXIS,
  type: 'linear',
  position: 'right',
  ticks: {
    min: 0,
    callback: (value) => durationToString(hoursToMs(value)),
  },
  gridLines: {
    lineWidth: 0,
  },
});

const getXAxisOptions = () => ({
  id: datasetConfig.xAxisID,
  type: 'category',
  position: 'left',
  ticks: {
    autoSkipPadding: 10,
  },
  gridLines: {
    display: false,
  },
});

export const useReviewTimeChart = (reviewers) => {
  const chartRef = useRef();
  const hasRef = !chartRef.current;

  useEffect(() => {
    if (!chartRef.current) return;

    const labels = reviewers.map((r) => r.name);

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: 16,
      },
      hover: {
        mode: 'x',
        intersect: false,
      },
      tooltips: getTooltipOptions(),
      scales: {
        yAxes: [getLeftYAxisOptions(), getRightYAxisOptions()],
        xAxes: [getXAxisOptions()],
      },
    };

    Chart.defaults.defaultFontColor = defaultFontColor;

    new Chart(chartRef.current, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          getTimeToReviewDataset(reviewers),
          getTotalReviewsDataset(reviewers),
          getTotalCommentsDataset(reviewers),
        ],
      },
      options,
    });
  }, [hasRef, reviewers]);

  return { chartRef };
};
